import React, { useState, useEffect, useCallback, useRef, KeyboardEvent } from 'react';
import styled from 'styled-components';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../store/authStore';
import glassTheme from '../../styles/glassTheme';
import { getAllUsers, getAnalytics, exportUserData, deleteUser, getPendingTeachers, approveTeacher, rejectTeacher, getUserRoleSummary, RoleSummary } from '../../services/adminApi';
import { User, AdminAnalytics } from '../../types';
import toast from 'react-hot-toast';
import { DashboardLayout } from '../../components/DashboardLayout';
import { AdminTrafficPanel } from './AdminTrafficPanel';

// Canonical system roles in priority order (mirrors server/seed/seed-rbac.js),
// used to organize "用户管理" by role instead of one undifferentiated list.
const ROLE_ORDER = ['student', 'teacher', 'super_admin', 'admin', 'moderator', 'analyst', 'content_manager'] as const;
type RoleKey = typeof ROLE_ORDER[number];

const ROLE_LABELS: Record<RoleKey, string> = {
  super_admin: '超级管理员',
  admin: '管理员',
  moderator: '内容审核员',
  analyst: '数据分析员',
  content_manager: '内容管理员',
  teacher: '教师',
  student: '学生'
};

const ROLE_BADGE_TYPES: Record<RoleKey, 'success' | 'warning' | 'info' | 'purple'> = {
  super_admin: 'success',
  admin: 'success',
  moderator: 'purple',
  analyst: 'purple',
  content_manager: 'purple',
  teacher: 'warning',
  student: 'info'
};

const getRoleLabel = (role?: string): string =>
  (role && ROLE_LABELS[role as RoleKey]) || '学生';

const getRoleBadgeType = (role?: string): 'success' | 'warning' | 'info' | 'purple' =>
  (role && ROLE_BADGE_TYPES[role as RoleKey]) || 'info';

const Tabs = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 2rem;
  overflow-x: auto;
  /* overflow-x: auto makes overflow-y compute to auto rather than staying
     visible, so a child overflowing upward gets clipped. Reserve room for the
     notification badge's negative offset instead of letting it be cut off. */
  padding-top: 10px;
`;

const Tab = styled.button.withConfig({
  shouldForwardProp: (prop) => prop !== 'active'
})<{ active: boolean }>`
  padding: 1rem 2rem;
  background: transparent;
  color: ${props => props.active ? glassTheme.colors.text.primary : glassTheme.colors.text.secondary};
  border: none;
  border-radius: 0;
  border-bottom: 2px solid ${props => props.active ? glassTheme.colors.accent.blue : 'transparent'};
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all ${glassTheme.transition.fast};
  display: flex;
  align-items: center;
  gap: 0.5rem;
  position: relative;
  min-height: 44px;

  &:hover {
    color: ${glassTheme.colors.text.primary};
    border-bottom-color: ${glassTheme.colors.accent.blue};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &:focus-visible {
    outline: 3px solid rgba(96, 165, 250, 0.3);
    outline-offset: 2px;
  }
`;

const CardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
`;

const StatCard = styled.div`
  background: ${glassTheme.glass.container};
  backdrop-filter: blur(20px);
  padding: 1.5rem;
  border-radius: 16px;
  border: 1px solid ${glassTheme.colors.border};
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.3);
`;

const StatLabel = styled.p`
  color: ${glassTheme.colors.text.secondary};
  font-size: 0.9rem;
  margin-bottom: 0.5rem;
`;

const StatValue = styled.p`
  font-size: 2.5rem;
  font-weight: bold;
  background: ${glassTheme.gradients.cyber};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 2rem;
`;

const NotificationBadge = styled.span`
  position: absolute;
  top: -8px;
  right: -8px;
  background: ${glassTheme.gradients.error};
  color: #fff;
  font-size: 0.7rem;
  font-weight: bold;
  padding: 0.2rem 0.5rem;
  border-radius: 10px;
  min-width: 1.2rem;
  text-align: center;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
  /* Paint above the tab label and its bottom border. */
  z-index: 2;
`;

const Button = styled.button<{ variant?: 'primary' | 'danger' | 'outline' }>`
  padding: 0.8rem 1.5rem;
  background: ${props => {
    switch (props.variant) {
      case 'primary':
        return glassTheme.gradients.cyber;
      case 'danger':
        return 'linear-gradient(135deg, #f87171, #ef4444)';
      default:
        return 'transparent';
    }
  }};
  color: #fff;
  border: ${props => props.variant === 'outline'
    ? `1px solid ${glassTheme.colors.border}`
    : 'none'
  };
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all ${glassTheme.transition.base};

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.3);
  }

  &:focus-visible {
    outline: 3px solid rgba(96, 165, 250, 0.3);
    outline-offset: 2px;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;


const TableContainer = styled.div`
  background: ${glassTheme.glass.container};
  backdrop-filter: blur(20px);
  padding: 2rem;
  border-radius: 16px;
  border: 1px solid ${glassTheme.colors.border};
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.3);
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TableHeader = styled.th`
  text-align: left;
  padding: 1rem;
  color: ${glassTheme.colors.text.secondary};
  font-weight: 600;
  border-bottom: 1px solid ${glassTheme.colors.border};
`;

const TableCell = styled.td`
  padding: 1rem;
  border-bottom: 1px solid ${glassTheme.colors.border};
  color: ${glassTheme.colors.text.primary};
`;

const TableRow = styled.tr`
  &:hover {
    background: rgba(96, 165, 250, 0.05);
  }
`;

const Badge = styled.span<{ type: 'success' | 'warning' | 'info' | 'purple' }>`
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;
  background: ${props => {
    switch (props.type) {
      case 'success':
        return 'rgba(74, 222, 128, 0.2)';
      case 'warning':
        return 'rgba(250, 204, 21, 0.2)';
      case 'purple':
        return 'rgba(192, 132, 252, 0.2)';
      default:
        return 'rgba(96, 165, 250, 0.2)';
    }
  }};
  color: ${props => {
    switch (props.type) {
      case 'success':
        return '#4ade80';
      case 'warning':
        return '#facc15';
      case 'purple':
        return '#c084fc';
      default:
        return '#60a5fa';
    }
  }};
`;

const RoleFilterBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin-bottom: 1.5rem;
`;

const RoleChip = styled.button<{ $active: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: all ${glassTheme.transition.fast};
  border: 1px solid ${props => props.$active ? glassTheme.colors.accent.blue : glassTheme.colors.border};
  background: ${props => props.$active ? 'rgba(96, 165, 250, 0.2)' : 'rgba(0, 0, 0, 0.3)'};
  color: ${props => props.$active ? glassTheme.colors.text.primary : glassTheme.colors.text.secondary};

  &:hover {
    border-color: ${glassTheme.colors.accent.blue};
    color: ${glassTheme.colors.text.primary};
  }
`;

const RoleChipCount = styled.span<{ $active: boolean }>`
  padding: 0.1rem 0.5rem;
  border-radius: 10px;
  font-size: 0.75rem;
  background: ${props => props.$active ? 'rgba(255, 255, 255, 0.2)' : 'rgba(255, 255, 255, 0.1)'};
`;

const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 200px;
  color: ${glassTheme.colors.text.primary};
`;

const Pagination = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 1rem;
  margin-top: 2rem;
`;

// Modal styles
const ModalOverlay = styled.div<{ $visible: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(4px);
  display: ${props => props.$visible ? 'flex' : 'none'};
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContainer = styled.div`
  background: ${glassTheme.glass.container};
  backdrop-filter: blur(20px);
  border-radius: 16px;
  border: 1px solid ${glassTheme.colors.border};
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
  padding: 2rem;
  min-width: 400px;
  max-width: 500px;
  animation: modalSlideIn 0.3s ease-out;

  @keyframes modalSlideIn {
    from {
      opacity: 0;
      transform: translateY(-20px) scale(0.95);
    }
    to {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
  }
`;

const ModalTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  color: ${glassTheme.colors.text.primary};
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const ModalContent = styled.div`
  color: ${glassTheme.colors.text.secondary};
  margin-bottom: 1.5rem;
  line-height: 1.6;
`;

const ModalDataList = styled.ul`
  margin: 1rem 0;
  padding-left: 1.5rem;
  color: ${glassTheme.colors.text.secondary};
`;

const ModalDataListItem = styled.li`
  margin-bottom: 0.5rem;
`;

const ModalButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
`;

const WarningText = styled.p`
  color: ${glassTheme.colors.accent.yellow};
  font-weight: 500;
  margin-top: 1rem;
  padding: 0.75rem;
  background: rgba(251, 146, 60, 0.1);
  border-radius: 8px;
  border: 1px solid rgba(251, 146, 60, 0.3);
`;

type TabType = 'overview' | 'traffic' | 'users' | 'teachers' | 'analytics';
const ADMIN_TABS: TabType[] = ['overview', 'traffic', 'users', 'teachers', 'analytics'];

export const AdminDashboard: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const locationRef = useRef(location);
  locationRef.current = location;
  const { user, logout } = useAuthStore();
  const [activeTab, setActiveTab] = useState<TabType>(() => {
    const requested = new URLSearchParams(location.search).get('tab');
    return ADMIN_TABS.includes(requested as TabType) ? requested as TabType : 'overview';
  });
  const [analytics, setAnalytics] = useState<AdminAnalytics | null>(null);
  const [users, setUsers] = useState<User[]>([]);
  const [pendingTeachers, setPendingTeachers] = useState<User[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState<string | null>(null);
  // Role the "用户管理" (users) tab is currently organized/filtered by.
  // 'all' shows every user; any other value scopes both the count and the
  // fetched page to that single role.
  const [roleFilter, setRoleFilter] = useState<string>('all');
  const [roleSummary, setRoleSummary] = useState<RoleSummary | null>(null);

  useEffect(() => {
    const requested = new URLSearchParams(location.search).get('tab');
    setActiveTab(ADMIN_TABS.includes(requested as TabType)
      ? requested as TabType
      : 'overview');
  }, [location.search]);

  useEffect(() => {
    const currentLocation = locationRef.current;
    const params = new URLSearchParams(currentLocation.search);
    if (params.get('tab') === activeTab) return;
    params.set('tab', activeTab);
    navigate({ pathname: currentLocation.pathname, search: `?${params.toString()}` }, { replace: true });
  }, [activeTab, navigate]);

  // If a request fails with 401, the stored token is invalid/expired (e.g.
  // session timeout). Log out and send the user back to login instead of
  // leaving them stuck on a dashboard that will keep failing every request.
  const handleAuthError = useCallback((error: any): boolean => {
    if (error?.response?.status === 401) {
      toast.error('登录已过期，请重新登录');
      logout(false);
      navigate('/');
      return true;
    }
    if (error?.response?.status === 403) {
      toast.error('Access denied. Admin privileges required.');
      navigate('/learn');
      return true;
    }
    return false;
  }, [logout, navigate]);

  const handleTabKeyDown = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
    let nextIndex: number | null = null;
    if (event.key === 'ArrowRight') nextIndex = (index + 1) % ADMIN_TABS.length;
    if (event.key === 'ArrowLeft') nextIndex = (index - 1 + ADMIN_TABS.length) % ADMIN_TABS.length;
    if (event.key === 'Home') nextIndex = 0;
    if (event.key === 'End') nextIndex = ADMIN_TABS.length - 1;
    if (nextIndex === null) return;
    event.preventDefault();
    const nextTab = ADMIN_TABS[nextIndex];
    setActiveTab(nextTab);
    document.getElementById(`admin-tab-${nextTab}`)?.focus();
  };

  // Delete user modal state
  const [deleteModal, setDeleteModal] = useState<{
    visible: boolean;
    step: 1 | 2;
    userId: string | null;
    userName: string;
  }>({
    visible: false,
    step: 1,
    userId: null,
    userName: ''
  });

  // Load pending teachers on mount for badge count
  useEffect(() => {
    loadPendingTeachers();
  }, []);

  useEffect(() => {
    // Check if user has admin or super_admin role
    const hasAdminRole = user?.roles?.some(role =>
      role.name === 'admin' || role.name === 'super_admin'
    ) || user?.primaryRole === 'admin' || user?.primaryRole === 'super_admin' ||
      user?.isAdmin === true;

    if (!hasAdminRole) {
      toast.error('Access denied. Admin privileges required.');
      navigate('/learn');
      return;
    }

    if (activeTab === 'overview' || activeTab === 'analytics') {
      loadAnalytics();
    } else if (activeTab === 'users') {
      loadUsers(currentPage, roleFilter);
      loadRoleSummary();
    } else if (activeTab === 'teachers') {
      loadPendingTeachers();
    }
  }, [activeTab, currentPage, roleFilter, user]);

  // Switching role should always restart from page 1, since the previous
  // page number may not exist within the newly selected role's result set.
  const handleRoleFilterChange = (role: string) => {
    setRoleFilter(role);
    setCurrentPage(1);
  };

  const loadRoleSummary = async () => {
    try {
      const data = await getUserRoleSummary();
      setRoleSummary(data);
    } catch (error: any) {
      if (!handleAuthError(error)) {
        console.error('Failed to load role summary:', error);
      }
    }
  };

  const loadAnalytics = async () => {
    try {
      setLoading(true);
      const data = await getAnalytics();
      setAnalytics(data);
    } catch (error: any) {
      if (!handleAuthError(error)) {
        toast.error(error.response?.data?.message || 'Failed to load analytics');
      }
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async (page: number, role: string = 'all') => {
    try {
      setLoading(true);
      const response = await getAllUsers(page, 20, role);
      setUsers(response.users);
      setTotalPages(response.pagination.pages);
    } catch (error: any) {
      if (!handleAuthError(error)) {
        toast.error(error.response?.data?.message || 'Failed to load users');
      }
    } finally {
      setLoading(false);
    }
  };

  const loadPendingTeachers = async () => {
    try {
      setLoading(true);
      const data = await getPendingTeachers();
      setPendingTeachers(data);
    } catch (error: any) {
      if (!handleAuthError(error)) {
        toast.error(error.response?.data?.message || 'Failed to load pending teachers');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (teacherId: string) => {
    try {
      setProcessing(teacherId);
      await approveTeacher(teacherId);
      toast.success('教师审核通过');
      // Re-fetch pending teachers from server to ensure sync
      await loadPendingTeachers();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to approve teacher');
    } finally {
      setProcessing(null);
    }
  };

  const handleReject = async (teacherId: string) => {
    const reason = prompt('请输入拒绝原因（可选）：');
    if (reason === null) return;

    try {
      setProcessing(teacherId);
      await rejectTeacher(teacherId, reason || undefined);
      toast.success('教师申请已拒绝');
      // Re-fetch pending teachers from server to ensure sync
      await loadPendingTeachers();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to reject teacher');
    } finally {
      setProcessing(null);
    }
  };

  const handleExport = async (format: 'csv' | 'json') => {
    try {
      await exportUserData(format, true);
      toast.success(`Data exported as ${format.toUpperCase()}`);
    } catch (error: any) {
      toast.error('Failed to export data');
    }
  };

  const handleDeleteUser = async (userId: string, userName: string) => {
    // Show step 1 of the modal
    setDeleteModal({
      visible: true,
      step: 1,
      userId,
      userName
    });
  };

  const confirmDeleteUser = async (deleteAllData: boolean) => {
    const { userId } = deleteModal;
    if (!userId) return;

    try {
      setProcessing(userId);
      await deleteUser(userId, deleteAllData);
      toast.success(deleteAllData ? '用户账户已删除，学习数据已归档保留' : '用户已删除');
      setDeleteModal({ visible: false, step: 1, userId: null, userName: '' });
      loadUsers(currentPage, roleFilter);
      loadRoleSummary();
    } catch (error: any) {
      toast.error(error.response?.data?.message || '删除用户失败');
    } finally {
      setProcessing(null);
    }
  };

  if (loading) {
    return (
      <DashboardLayout
        title="管理后台"
        subtitle="平台数据管理与用户分析"
      >
        <LoadingSpinner>Loading...</LoadingSpinner>
      </DashboardLayout>
    );
  }

  return (
    <>
      <DashboardLayout
        title="管理后台"
        subtitle="平台数据管理与用户分析"
    >
      <Tabs role="tablist" aria-label="管理后台栏目">
        <Tab id="admin-tab-overview" role="tab" aria-selected={activeTab === 'overview'} aria-controls="admin-panel-overview" tabIndex={activeTab === 'overview' ? 0 : -1} active={activeTab === 'overview'} onKeyDown={event => handleTabKeyDown(event, 0)} onClick={() => setActiveTab('overview')}>
          📊 概览
        </Tab>
        <Tab id="admin-tab-traffic" role="tab" aria-selected={activeTab === 'traffic'} aria-controls="admin-panel-traffic" tabIndex={activeTab === 'traffic' ? 0 : -1} active={activeTab === 'traffic'} onKeyDown={event => handleTabKeyDown(event, 1)} onClick={() => setActiveTab('traffic')}>
          📡 流量监控
        </Tab>
        <Tab id="admin-tab-users" role="tab" aria-selected={activeTab === 'users'} aria-controls="admin-panel-users" tabIndex={activeTab === 'users' ? 0 : -1} active={activeTab === 'users'} onKeyDown={event => handleTabKeyDown(event, 2)} onClick={() => setActiveTab('users')}>
          👥 用户管理
        </Tab>
        <Tab id="admin-tab-teachers" role="tab" aria-selected={activeTab === 'teachers'} aria-controls="admin-panel-teachers" tabIndex={activeTab === 'teachers' ? 0 : -1} active={activeTab === 'teachers'} onKeyDown={event => handleTabKeyDown(event, 3)} onClick={() => setActiveTab('teachers')}>
          👨‍🏫 教师审核
          {pendingTeachers.length > 0 && activeTab !== 'teachers' && <NotificationBadge>{pendingTeachers.length}</NotificationBadge>}
        </Tab>
        <Tab id="admin-tab-analytics" role="tab" aria-selected={activeTab === 'analytics'} aria-controls="admin-panel-analytics" tabIndex={activeTab === 'analytics' ? 0 : -1} active={activeTab === 'analytics'} onKeyDown={event => handleTabKeyDown(event, 4)} onClick={() => setActiveTab('analytics')}>
          📈 数据分析
        </Tab>
      </Tabs>

        {activeTab === 'overview' && analytics && (
          <div id="admin-panel-overview" role="tabpanel" aria-labelledby="admin-tab-overview">
            <CardGrid>
              <StatCard>
                <StatLabel>总用户数</StatLabel>
                <StatValue>{analytics.overview.totalUsers}</StatValue>
              </StatCard>
              <StatCard>
                <StatLabel>完善信息用户</StatLabel>
                <StatValue>{analytics.overview.usersWithInfo}</StatValue>
              </StatCard>
              <StatCard>
                <StatLabel>信息完成率</StatLabel>
                <StatValue>{analytics.overview.completionRate}%</StatValue>
              </StatCard>
              <StatCard>
                <StatLabel>有统计学背景</StatLabel>
                <StatValue>{analytics.demographics.statsBackground.yes}</StatValue>
              </StatCard>
            </CardGrid>

            <ButtonGroup>
              <Button variant="primary" onClick={() => handleExport('json')}>
                导出JSON
              </Button>
              <Button variant="outline" onClick={() => handleExport('csv')}>
                导出CSV
              </Button>
            </ButtonGroup>

            <h3 style={{ color: glassTheme.colors.text.primary, marginBottom: '1rem' }}>
              最近注册用户
            </h3>
            <TableContainer>
              <Table>
                <thead>
                  <tr>
                    <TableHeader>用户名</TableHeader>
                    <TableHeader>邮箱</TableHeader>
                    <TableHeader>信息状态</TableHeader>
                    <TableHeader>注册时间</TableHeader>
                  </tr>
                </thead>
                <tbody>
                  {analytics.recentUsers.map((u: User) => (
                    <TableRow key={u.id}>
                      <TableCell>{u.name}</TableCell>
                      <TableCell>{u.email}</TableCell>
                      <TableCell>
                        {u.infoCompleted ? (
                          <Badge type="success">已完成</Badge>
                        ) : (
                          <Badge type="warning">未完成</Badge>
                        )}

                      </TableCell>
                      <TableCell>{new Date(u.createdAt).toLocaleDateString()}</TableCell>
                    </TableRow>
                  ))}
                </tbody>
              </Table>
            </TableContainer>
          </div>
        )}

        {activeTab === 'traffic' && (
          <div
            id="admin-panel-traffic"
            role="tabpanel"
            aria-labelledby="admin-tab-traffic"
          >
            <AdminTrafficPanel onAuthError={handleAuthError} />
          </div>
        )}

        {activeTab === 'users' && (
          <div id="admin-panel-users" role="tabpanel" aria-labelledby="admin-tab-users">
            <ButtonGroup>
              <Button variant="primary" onClick={() => handleExport('json')}>
                导出JSON
              </Button>
              <Button variant="outline" onClick={() => handleExport('csv')}>
                导出CSV
              </Button>
            </ButtonGroup>

            <RoleFilterBar role="tablist" aria-label="按角色筛选用户">
              <RoleChip
                role="tab"
                aria-selected={roleFilter === 'all'}
                $active={roleFilter === 'all'}
                onClick={() => handleRoleFilterChange('all')}
              >
                全部
                <RoleChipCount $active={roleFilter === 'all'}>{roleSummary?.total ?? '—'}</RoleChipCount>
              </RoleChip>
              {ROLE_ORDER.map((role) => {
                const count = roleSummary?.roles.find(r => r.role === role)?.count ?? 0;
                return (
                  <RoleChip
                    key={role}
                    role="tab"
                    aria-selected={roleFilter === role}
                    $active={roleFilter === role}
                    onClick={() => handleRoleFilterChange(role)}
                  >
                    {ROLE_LABELS[role]}
                    <RoleChipCount $active={roleFilter === role}>{count}</RoleChipCount>
                  </RoleChip>
                );
              })}
            </RoleFilterBar>

            <TableContainer>
              <Table>
                <thead>
                  <tr>
                    <TableHeader>用户名</TableHeader>
                    <TableHeader>邮箱</TableHeader>
                    <TableHeader>角色</TableHeader>
                    <TableHeader>真实姓名</TableHeader>
                    <TableHeader>学院</TableHeader>
                    <TableHeader>专业</TableHeader>
                    <TableHeader>统计学背景</TableHeader>
                    <TableHeader>操作</TableHeader>
                  </tr>
                </thead>
                <tbody>
                  {users.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={8} style={{ textAlign: 'center', color: glassTheme.colors.text.muted }}>
                        {roleFilter === 'all' ? '暂无用户' : `暂无${getRoleLabel(roleFilter)}用户`}
                      </TableCell>
                    </TableRow>
                  )}
                  {users.map((u) => (
                    <TableRow key={u.id}>
                      <TableCell>{u.name}</TableCell>
                      <TableCell>{u.email}</TableCell>
                      <TableCell>
                        <Badge type={getRoleBadgeType(u.primaryRole)}>
                          {getRoleLabel(u.primaryRole)}
                        </Badge>
                      </TableCell>
                      <TableCell>{u.studentInfo?.realName || '-'}</TableCell>
                      <TableCell>{u.studentInfo?.college || '-'}</TableCell>
                      <TableCell>{u.studentInfo?.major || '-'}</TableCell>
                      <TableCell>
                        {u.studentInfo?.hasStatsBackground ? (
                          <Badge type="success">有</Badge>
                        ) : (
                          <Badge type="warning">无</Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="danger"
                          onClick={() => handleDeleteUser(u.id, u.name)}
                        >
                          删除
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </tbody>
              </Table>
            </TableContainer>

            <Pagination>
              <Button
                variant="outline"
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
              >
                上一页
              </Button>
              <span style={{ color: glassTheme.colors.text.primary }}>
                第 {currentPage} / {totalPages} 页
              </span>
              <Button
                variant="outline"
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
              >
                下一页
              </Button>
            </Pagination>
          </div>
        )}

        {activeTab === 'teachers' && (
          <div id="admin-panel-teachers" role="tabpanel" aria-labelledby="admin-tab-teachers">
            <h3 style={{ color: glassTheme.colors.text.primary, marginBottom: '1rem' }}>
              待审核教师 ({pendingTeachers.length})
            </h3>
            {pendingTeachers.length === 0 ? (
              <TableContainer>
                <div style={{ textAlign: 'center', padding: '3rem', color: glassTheme.colors.text.secondary }}>
                  <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>✅</div>
                  <div>暂无待审核的教师申请</div>
                </div>
              </TableContainer>
            ) : (
              <TableContainer>
                <Table>
                  <thead>
                    <tr>
                      <TableHeader>姓名</TableHeader>
                      <TableHeader>邮箱</TableHeader>
                      <TableHeader>所属院校</TableHeader>
                      <TableHeader>院系/部门</TableHeader>
                      <TableHeader>注册时间</TableHeader>
                      <TableHeader>操作</TableHeader>
                    </tr>
                  </thead>
                  <tbody>
                    {pendingTeachers.map((teacher) => (
                      <TableRow key={teacher.id}>
                        <TableCell>{teacher.name}</TableCell>
                        <TableCell>{teacher.email}</TableCell>
                        <TableCell>{teacher.teacherInfo?.institution || '-'}</TableCell>
                        <TableCell>{teacher.teacherInfo?.department || '-'}</TableCell>
                        <TableCell>{new Date(teacher.createdAt).toLocaleDateString('zh-CN')}</TableCell>
                        <TableCell>
                          <Button
                            variant="primary"
                            onClick={() => handleApprove(teacher.id)}
                            disabled={processing === teacher.id}
                            style={{ marginRight: '0.5rem' }}
                          >
                            {processing === teacher.id ? '处理中...' : '通过'}
                          </Button>
                          <Button
                            variant="danger"
                            onClick={() => handleReject(teacher.id)}
                            disabled={processing === teacher.id}
                          >
                            拒绝
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </tbody>
                </Table>
              </TableContainer>
            )}
          </div>
        )}

        {activeTab === 'analytics' && analytics && (
          <div id="admin-panel-analytics" role="tabpanel" aria-labelledby="admin-tab-analytics">
            <CardGrid>
              <StatCard>
                <StatLabel>性别分布</StatLabel>
                {analytics.demographics.byGender.map((g: { _id: string; count: number }) => (
                  <div key={g._id} style={{ marginTop: '0.5rem' }}>
                    <span style={{ color: glassTheme.colors.text.primary }}>
                      {g._id === 'male' ? '男' : '女'}: {g.count}
                    </span>
                  </div>
                ))}
              </StatCard>

              <StatCard>
                <StatLabel>统计学水平</StatLabel>
                {analytics.demographics.byStatsLevel.map((l: { _id: string; count: number }) => (
                  <div key={l._id} style={{ marginTop: '0.5rem' }}>
                    <span style={{ color: glassTheme.colors.text.primary }}>
                      {l._id}: {l.count}
                    </span>
                  </div>
                ))}
              </StatCard>

              <StatCard>
                <StatLabel>Top 10 学院</StatLabel>
                {analytics.demographics.byCollege.map((c: { _id: string; count: number }) => (
                  <div key={c._id} style={{ marginTop: '0.5rem' }}>
                    <span style={{ color: glassTheme.colors.text.primary }}>
                      {c._id}: {c.count}
                    </span>
                  </div>
                ))}
              </StatCard>

              <StatCard>
                <StatLabel>Top 10 专业</StatLabel>
                {analytics.demographics.byMajor.map((m: { _id: string; count: number }) => (
                  <div key={m._id} style={{ marginTop: '0.5rem' }}>
                    <span style={{ color: glassTheme.colors.text.primary }}>
                      {m._id}: {m.count}
                    </span>
                  </div>
                ))}
              </StatCard>
            </CardGrid>
          </div>
        )}
      </DashboardLayout>

      {/* Delete User Confirmation Modal */}
      <ModalOverlay $visible={deleteModal.visible} onClick={() => setDeleteModal({ visible: false, step: 1, userId: null, userName: '' })}>
        <ModalContainer onClick={(e) => e.stopPropagation()}>
          {deleteModal.step === 1 ? (
            <>
              <ModalTitle>⚠️ 删除用户确认</ModalTitle>
              <ModalContent>
                确定要删除用户 "<strong>{deleteModal.userName}</strong>" 吗？
                <WarningText>
                  此操作无法撤销
                </WarningText>
              </ModalContent>
              <ModalButtonGroup>
                <Button variant="outline" onClick={() => setDeleteModal({ visible: false, step: 1, userId: null, userName: '' })}>
                  取消
                </Button>
                <Button
                  variant="danger"
                  onClick={() => setDeleteModal({ ...deleteModal, step: 2 })}
                  disabled={processing === deleteModal.userId}
                >
                  {processing === deleteModal.userId ? '处理中...' : '确认删除'}
                </Button>
              </ModalButtonGroup>
            </>
          ) : (
            <>
              <ModalTitle>🗑️ 清理账户关联</ModalTitle>
              <ModalContent>
                该用户的学习数据将被保留（学生的学习记录很宝贵，不会被删除）。
                是否同时将以下关联数据标记为"已归档"，并从课程/主题的学生列表中移除该用户？
                <ModalDataList>
                  <ModalDataListItem>📚 学生档案（保留，标记归档）</ModalDataListItem>
                  <ModalDataListItem>📊 学习事件记录（保留，标记归档）</ModalDataListItem>
                  <ModalDataListItem>📖 课程注册记录（从课程学生名单中移除）</ModalDataListItem>
                  <ModalDataListItem>✅ 主题完成状态（从主题完成名单中移除）</ModalDataListItem>
                </ModalDataList>
                <WarningText>
                  用户账户（登录凭证）将被永久删除，但学习数据会被保留归档，可供管理员或研究用途查阅
                </WarningText>
              </ModalContent>
              <ModalButtonGroup>
                <Button
                  variant="outline"
                  onClick={() => confirmDeleteUser(false)}
                  disabled={processing === deleteModal.userId}
                >
                  仅删除账户
                </Button>
                <Button
                  variant="danger"
                  onClick={() => confirmDeleteUser(true)}
                  disabled={processing === deleteModal.userId}
                >
                  {processing === deleteModal.userId ? '处理中...' : '删除账户并归档数据'}
                </Button>
              </ModalButtonGroup>
            </>
          )}
        </ModalContainer>
      </ModalOverlay>
    </>
  );
};
